/**
 * Player Schedule Filter (V066)
 *
 * Decides if the player itself is "open" RIGHT NOW based on its own schedule
 * (tab_players.schedule). 5ª camada AND no filtro de playlist.
 *
 * <p>Architecture (decisão Renato — Socratic Gate V066):</p>
 *
 *   Server (`MediaSyncService`):
 *     Anexa `playerSchedule` em TODO sync (full + hash short-circuit).
 *     Schedule do player pode mudar sem mudar library_hash, então propaga
 *     sempre.
 *
 *   Cliente (este arquivo):
 *     - Single object (player tem 1 schedule próprio).
 *     - <b>Sem bypass</b> — diferente do Grupo (V064): janela do player aplica
 *       a TUDO (inclusive associação direta). Operador escolheu (B) — AND puro.
 *     - Composição c/ Grupo: AND (intersecção). Player 10-16 ∩ Grupo 8-18 → 10-16.
 *     - Default: schedule = null OU todos campos NULL/empty ⇒ player liberado 24/7.
 *       Players legados (criados antes V066) ficam todos liberados — back-compat.
 *
 *   Composição AND completa no player (5 camadas):
 *     Conteúdo (V059) ∩ PlaylistItem ∩ Campanha ∩ Grupo (V060) ∩ Player (V066)
 *
 *   <p><b>R1 do guardrail (tab_stores não influencia player) MANTIDA</b> — esta
 *   é configuração explícita do PRÓPRIO player (tab_players.schedule), não da
 *   loja. Diferente de tab_stores que descreve horário de funcionamento da
 *   loja física.</p>
 */

import { PlayerSchedule } from '../types';
import {
  getCurrentTimeInTimezone,
  isDayActive,
  isTimeInRange,
} from './scheduleParser';

const WEEKDAY_CODE_TO_INDEX: Record<string, number> = {
  SUN: 0,
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6,
};

const WEEKDAY_INDEX_TO_CODE: Record<number, string> = {
  0: 'SUN',
  1: 'MON',
  2: 'TUE',
  3: 'WED',
  4: 'THU',
  5: 'FRI',
  6: 'SAT',
};

const DEFAULT_TZ = 'America/Sao_Paulo';

export interface PlayerScheduleDecision {
  /** true if player can play content RIGHT NOW. */
  isPlayable: boolean;
  /** null if isPlayable=true; otherwise human-friendly reason (debug/log/UI). */
  reason: string | null;
}

function normalizeWeekdayCode(raw: string): string {
  return raw.trim().toUpperCase().slice(0, 3);
}

/**
 * Avalia se o player está dentro da sua janela de exibição AGORA.
 *
 * <p>Reusa exatamente a mesma máquina de SIMPLE/ADVANCED do grupo (V064) e do
 * conteúdo (V059) — só muda o "sujeito" da janela. Preserva normalização
 * Tizen hour=24 via `isTimeInRange` (`scheduleParser.ts`).</p>
 *
 * <p>NULL/undefined → liberado (back-compat). Equivalente a "sem schedule".</p>
 *
 * <p>2026-06-12 — `validFrom`/`validUntil` agora SÃO avaliados aqui no fuso
 * LOCAL do device (decisão Renato — R0 em regras-temporais-player.md).
 * Server sempre envia o schedule (com validade); cliente faz o "afunilamento
 * final" respeitando o fuso de cada player.</p>
 */
export function isPlayerCurrentlyOpen(
  schedule: PlayerSchedule | null | undefined,
  now: Date = new Date(),
): PlayerScheduleDecision {
  // NULL = liberado (player legado ou sem schedule configurado).
  if (!schedule) {
    return { isPlayable: true, reason: null };
  }

  // EARLY RETURN — validade no fuso LOCAL do device (vide R0).
  const validity = checkValidityLocal(
    schedule.validFrom,
    schedule.validUntil,
    now,
  );
  if (!validity.isPlayable) {
    return validity;
  }

  const mode = schedule.scheduleMode;

  // ADVANCED mode — uses weeklySchedule
  if (mode === 'ADVANCED') {
    const weekly = schedule.weeklySchedule;
    if (!weekly || Object.keys(weekly).length === 0) {
      // Defesa conservadora: ADVANCED sem weeklySchedule = fechado.
      // Server bloqueia esse caso via CHECK constraint, mas se vazar
      // (dado corrupto/legado), cliente trata como bloqueado pra ser explícito.
      return {
        isPlayable: false,
        reason: 'ADVANCED sem weeklySchedule (config inconsistente)',
      };
    }
    const { hours, minutes, dayOfWeek } = getCurrentTimeInTimezone(
      DEFAULT_TZ,
      now,
    );
    const todayCode = WEEKDAY_INDEX_TO_CODE[dayOfWeek];
    const dayConfig = weekly[todayCode];
    if (!dayConfig) {
      return {
        isPlayable: false,
        reason: `dia ${todayCode} não configurado em weeklySchedule`,
      };
    }
    try {
      const inRange = isTimeInRange(
        { hours, minutes },
        dayConfig.startAt,
        dayConfig.endAt,
      );
      return {
        isPlayable: inRange,
        reason: inRange
          ? null
          : `hora ${hours}:${minutes} fora de ${dayConfig.startAt}-${dayConfig.endAt} (${todayCode})`,
      };
    } catch (err) {
      return {
        isPlayable: false,
        reason: `weeklySchedule[${todayCode}] inválido: ${(err as Error).message}`,
      };
    }
  }

  // SIMPLE mode (default) — daysOfWeek (lowercase) + startAt/endAt.
  const daysRaw = schedule.daysOfWeek;
  const startAt = schedule.startAt;
  const endAt = schedule.endAt;

  const hasDayFilter = daysRaw && daysRaw.length > 0;
  const hasTimeFilter =
    !!startAt && !!endAt && !(startAt === '00:00' && endAt === '23:59');

  // Player sem nenhum filtro = sempre aberto (criado na V066 sem opt-in).
  if (!hasDayFilter && !hasTimeFilter) {
    return { isPlayable: true, reason: null };
  }

  const { hours, minutes, dayOfWeek } = getCurrentTimeInTimezone(
    DEFAULT_TZ,
    now,
  );

  if (hasDayFilter) {
    const dayIndices = daysRaw!
      .map((d) => WEEKDAY_CODE_TO_INDEX[normalizeWeekdayCode(d)])
      .filter((idx) => idx !== undefined);
    if (!isDayActive(dayOfWeek, dayIndices)) {
      const todayCode = WEEKDAY_INDEX_TO_CODE[dayOfWeek];
      return {
        isPlayable: false,
        reason: `dia ${todayCode} ∉ daysOfWeek=[${daysRaw!.join(',')}]`,
      };
    }
  }

  if (hasTimeFilter) {
    try {
      const inRange = isTimeInRange({ hours, minutes }, startAt!, endAt!);
      if (!inRange) {
        return {
          isPlayable: false,
          reason: `hora ${hours}:${minutes} fora de ${startAt}-${endAt}`,
        };
      }
    } catch (err) {
      return {
        isPlayable: false,
        reason: `startAt/endAt inválido: ${(err as Error).message}`,
      };
    }
  }

  return { isPlayable: true, reason: null };
}

/**
 * 2026-06-12 — Valida `validFrom`/`validUntil` (formato "YYYY-MM-DD") no
 * fuso LOCAL do device. Helper compartilhado pra Player (V066) e Grupo (V060).
 *
 * <p>Player em Manaus (GMT-4) interpreta "12/06" como "início/fim do dia
 * 12/06 LOCAL" — não BRT. Server tem margem assimétrica (Noronha
 * pra validFrom, Acre pra validUntil); player faz o "afunilamento final".</p>
 */
function checkValidityLocal(
  validFrom: string | null | undefined,
  validUntil: string | null | undefined,
  now: Date,
): PlayerScheduleDecision {
  if (validFrom) {
    const start = parseLocalStartOfDay(validFrom);
    if (start && now < start) {
      return {
        isPlayable: false,
        reason: `validFrom ${validFrom} ainda no futuro (fuso local)`,
      };
    }
  }
  if (validUntil) {
    const end = parseLocalEndOfDay(validUntil);
    if (end && now > end) {
      return {
        isPlayable: false,
        reason: `validUntil ${validUntil} já passou (fuso local)`,
      };
    }
  }
  return { isPlayable: true, reason: null };
}

function parseLocalStartOfDay(dateStr: string): Date | null {
  const parts = dateStr.split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  return new Date(parts[0], parts[1] - 1, parts[2], 0, 0, 0, 0);
}

function parseLocalEndOfDay(dateStr: string): Date | null {
  const parts = dateStr.split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  return new Date(parts[0], parts[1] - 1, parts[2], 23, 59, 59, 999);
}
